
import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Loader2, Play, Bot, Cpu, AlertCircle } from 'lucide-react';
import { AgentCapability } from '@/types/agentic';
import { useAgentCapability } from '@/hooks/useAgentCapability';
import { hasCapabilityPermission } from '@/services/agentic/permissionService';
import { toast } from 'sonner';

interface CapabilityTesterProps {
  capability: AgentCapability;
}

const CapabilityTester: React.FC<CapabilityTesterProps> = ({ capability }) => {
  const [query, setQuery] = useState('');
  const [provider, setProvider] = useState<'openai' | 'anthropic'>('openai');
  const [model, setModel] = useState(provider === 'openai' ? 'gpt-4o-mini' : 'claude-3-5-sonnet-latest');
  const [hasExecutePermission, setHasExecutePermission] = useState(false);
  const [permissionLoading, setPermissionLoading] = useState(true);
  
  const { 
    loading, 
    result, 
    error, 
    executeCapability 
  } = useAgentCapability(capability, { defaultProvider: provider, defaultModel: model });

  useEffect(() => {
    checkExecutePermission();
  }, [capability.id]);

  const checkExecutePermission = async () => {
    setPermissionLoading(true);
    try {
      const canExecute = await hasCapabilityPermission(capability.id, 'execute');
      setHasExecutePermission(canExecute);
    } catch (error) {
      console.error('Error checking execute permission:', error);
      setHasExecutePermission(false);
    } finally {
      setPermissionLoading(false);
    }
  };

  const handleProviderChange = (value: 'openai' | 'anthropic') => {
    setProvider(value);
    // Update default model when provider changes
    setModel(value === 'openai' ? 'gpt-4o-mini' : 'claude-3-5-sonnet-latest');
  };

  const handleExecute = async () => {
    if (!query.trim()) return;
    
    if (!hasExecutePermission) {
      toast.error("You don't have permission to execute this capability");
      return;
    }
    
    try {
      await executeCapability(query, {
        provider,
        model
      });
    } catch (error) {
      console.error('Error executing capability:', error);
    }
  };

  // Helper to prettify JSON
  const prettifyJson = (json: any): string => {
    try {
      return JSON.stringify(json, null, 2);
    } catch (e) {
      return JSON.stringify(json);
    }
  };

  if (permissionLoading) {
    return (
      <div className="flex justify-center p-6">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!hasExecutePermission) {
    return (
      <div className="flex flex-col items-center justify-center p-6 gap-2 text-center">
        <AlertCircle className="h-10 w-10 text-amber-500" />
        <h3 className="text-lg font-medium mt-2">No Execute Permission</h3>
        <p className="text-muted-foreground max-w-md">
          You don't have permission to execute this capability.
          Please contact an administrator if you believe you should have access.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2">
        <Cpu className="h-5 w-5 text-primary" />
        <span className="font-medium text-lg">{capability.name}</span>
        <Badge variant="outline">{capability.capability_type.replace('_', ' ')}</Badge>
      </div>
      
      {capability.description && (
        <p className="text-muted-foreground">{capability.description}</p>
      )}
      
      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">Provider</label>
            <Select value={provider} onValueChange={(value: 'openai' | 'anthropic') => handleProviderChange(value)}>
              <SelectTrigger>
                <SelectValue placeholder="Select provider" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="openai">OpenAI</SelectItem>
                <SelectItem value="anthropic">Anthropic (Claude)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div className="space-y-2">
            <label className="text-sm font-medium">Model</label>
            <Select value={model} onValueChange={setModel}>
              <SelectTrigger>
                <SelectValue placeholder="Select model" />
              </SelectTrigger>
              <SelectContent>
                {provider === 'openai' ? (
                  <>
                    <SelectItem value="gpt-4o-mini">GPT-4o Mini</SelectItem>
                    <SelectItem value="gpt-4o">GPT-4o</SelectItem>
                  </>
                ) : (
                  <>
                    <SelectItem value="claude-3-5-sonnet-latest">Claude 3.5 Sonnet</SelectItem>
                    <SelectItem value="claude-3-opus-latest">Claude 3 Opus</SelectItem>
                    <SelectItem value="claude-3-sonnet-latest">Claude 3 Sonnet</SelectItem>
                    <SelectItem value="claude-3-haiku-latest">Claude 3 Haiku</SelectItem>
                  </>
                )}
              </SelectContent>
            </Select>
          </div>
        </div>
        
        <div className="space-y-2">
          <label className="text-sm font-medium">Query</label>
          <Textarea
            placeholder={`Enter your query for the "${capability.name}" capability...`}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            rows={4}
            className="resize-none"
          />
        </div>
        
        <Button 
          onClick={handleExecute} 
          disabled={loading || !query.trim() || !hasExecutePermission}
          className="w-full"
        >
          {loading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Executing...
            </>
          ) : (
            <>
              <Play className="mr-2 h-4 w-4" />
              Execute Capability
            </>
          )}
        </Button>
        
        {error && (
          <Card className="border-destructive">
            <CardContent className="p-4">
              <p className="text-destructive font-medium">Error</p>
              <pre className="mt-2 text-sm overflow-auto p-2 bg-destructive/10 rounded-md">
                {error.message}
              </pre>
            </CardContent>
          </Card>
        )}
        
        {result && (
          <div className="space-y-4">
            {result.toolExecution && (
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Cpu className="h-4 w-4 text-primary" />
                    <p className="font-medium">Tool Execution Result</p>
                  </div>
                  <pre className="text-sm overflow-auto p-2 bg-muted rounded-md">
                    {prettifyJson(result.toolExecution)}
                  </pre>
                </CardContent>
              </Card>
            )}
            
            {result.finalResponse && (
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Bot className="h-4 w-4 text-primary" />
                    <p className="font-medium">AI Response</p>
                  </div>
                  <div className="mt-2 text-sm p-2 bg-muted rounded-md">
                    {provider === 'openai' ? (
                      <div className="whitespace-pre-wrap">
                        {result.finalResponse.choices[0].message.content}
                      </div>
                    ) : (
                      <div className="whitespace-pre-wrap">
                        {result.finalResponse.content[0].text}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CapabilityTester;
